import {
  Component,
  State,
  h,
  Prop,
  Element,
  Method,
  Listen,
  EventEmitter,
  Event
} from '@stencil/core';

@Component({
  tag: 'uui-multi-select',
  shadow: false,
  scoped: true,
  styleUrl: 'uui-multi-select.css'
})
export class UuiSelect {
  /**
   * The underlying HTML element of this select.
   */
  @Element()
  el: HTMLElement;
  
  /**
   * default label to display when no options have been selected
   */
  @Prop() defaultLabel: string = 'Select an option';

  /**
   * state for managing visibility state for dropdown
   */
  @State() dropdownVisible: boolean;

  /**
   * state for managing the label to display
   */
  @State() selectedOptionsLabel: string;

  /**
   * passes back the selected items as a string array.
   */
  @Event({ bubbles: true, composed: true }) selectChange?: EventEmitter<
    string[]
  >;

  selectDropdown: HTMLElement;

  toggleMenu() {
    const allDropdowns = document.querySelectorAll('uui-multi-select');
    
    // close all other dropdowns on the page. 
    allDropdowns.forEach((dropdown) => {

      // ignore the current dropdown
      if(this.el === dropdown) return;
      dropdown?.close();
    });

    this.dropdownVisible = !this.dropdownVisible;
  }

  /**
   * closes dialog
   */
  @Method()
  async close() {
    this.dropdownVisible = false;
  }

  updateSelectedItems(event, emitSelectChangeEvent = false) {
    const name = event?.target.getAttribute('name') ?? '';
    const currentLabel = event?.target.getAttribute('label') ?? '';
    const checked = event?.target.checked ?? false;

    let selected = [];
    let selectedLabels = [];

    // get all currently selected elements
    const selectedElements = this.el.querySelectorAll(
      'uui-checkbox[aria-checked="true"]'
    );

    selectedElements.forEach(ele => {
      selected.push(ele.getAttribute('name'));
      selectedLabels.push(ele.getAttribute('label'));
    });

    if (checked) {
      selected.push(name);
      selectedLabels.push(currentLabel);
    } else {
      selected = selected.filter(s => s !== name);
      selectedLabels = selectedLabels.filter(s => s !== currentLabel);
    }

    const label = selectedLabels.reduce(
      (prevValue, currentValue, index, array) => {
        if (index === array.length - 1) {
          return prevValue + currentValue;
        }

        return prevValue + `${currentValue}, `;
      },
      ''
    );

    this.selectedOptionsLabel = label;
    if (emitSelectChangeEvent) {
      this.selectChange.emit(selected);
    }
  }

  @Listen('toggleChange')
  onOptionSelect(event) {
    this.updateSelectedItems(event, true);
  }


  @Listen('checkedChange')
  onCheckedChange() {
    this.updateSelectedItems(null, false);
  }

  outsideClick(e: PointerEvent | MouseEvent) {
    if (!this.el) {
      return false;
    }

    const isChild = this.el.contains(e.target as Node);
    return !isChild;
  }

  componentDidLoad() {
    this.el.addEventListener('click', (e: PointerEvent) => {
      e.stopPropagation();
    });

    document.addEventListener('click', (event: MouseEvent) => {
      const outsideClick = this.outsideClick(event);

      if (outsideClick) {
        this.close();
      }

    });

    this.updateSelectedItems(null, false);
  }

  render() {
    return (
      <div
        class={`uui-multi-select__container ${
          this.dropdownVisible ? 'uui-multi-select-dropdown__expanded' : ''
        }`}
      >
        <button
          role="combobox"
          class="uui-multi-select__button"
          onClick={this.toggleMenu.bind(this)}
          aria-haspopup="listbox"
          aria-expanded="false"
          aria-controls="select-dropdown"
        >
          <span class="selected-value" title={this.selectedOptionsLabel}>
            {this.selectedOptionsLabel?.length > 0
              ? this.selectedOptionsLabel
              : this.defaultLabel}
          </span>
          <span class="arrow"></span>
        </button>
        <ul
          role="listbox"
          id="select-dropdown"
          class={`uui-multi-select-dropdown__container ${
            this.dropdownVisible ? '' : 'hide'
          }`}
        >
          <slot />
        </ul>
      </div>
    );
  }
}
